import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { savedWords as seedWords } from "../data/savedWords";
import type { SavedWord, WordSource } from "../types/words";
import { normalize } from "../utils/text";
interface SaveInput {
  term: string;
  translation: string;
  courseId: string;
  source: WordSource;
}
interface WordsContextValue {
  words: SavedWord[];
  saveWord: (input: SaveInput) => {
    word: SavedWord;
    created: boolean;
  };
  removeWord: (id: string) => void;
  isSaved: (term: string, courseId: string) => boolean;
  recordPractice: (terms: string[], correct: boolean) => void;
  dueWords: (courseId?: string) => SavedWord[];
}
const WordsContext = createContext<WordsContextValue | null>(null);
function todayLabel(): string {
  return new Date().toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit"
  });
}
export function WordsProvider({
  children
}: {
  children: ReactNode;
}) {
  const [words, setWords] = useState<SavedWord[]>(seedWords);
  const isSaved = useCallback((term: string, courseId: string) => words.some(w => w.courseId === courseId && normalize(w.term) === normalize(term)), [words]);
  const saveWord = useCallback((input: SaveInput) => {
    const existing = words.find(w => w.courseId === input.courseId && normalize(w.term) === normalize(input.term));
    if (existing) return {
      word: existing,
      created: false
    };
    const word: SavedWord = {
      id: `w-${Date.now()}`,
      term: input.term,
      translation: input.translation,
      courseId: input.courseId,
      status: "new",
      source: input.source,
      usedIn: 0,
      timesCorrect: 0,
      nextReview: "Today",
      savedAt: todayLabel()
    };
    setWords(prev => [word, ...prev]);
    return {
      word,
      created: true
    };
  }, [words]);
  const removeWord = useCallback((id: string) => {
    setWords(prev => prev.filter(w => w.id !== id));
  }, []);
  const recordPractice = useCallback((terms: string[], correct: boolean) => {
    const keys = new Set(terms.map(normalize));
    setWords(prev => prev.map(w => {
      if (!keys.has(normalize(w.term))) return w;
      const timesCorrect = w.timesCorrect + (correct ? 1 : 0);
      return {
        ...w,
        usedIn: w.usedIn + 1,
        timesCorrect,
        status: timesCorrect >= 5 ? "mastered" : "practising",
        nextReview: correct ? "Tomorrow" : "Today"
      };
    }));
  }, []);
  const dueWords = useCallback((courseId?: string) => words.filter(w => w.status !== "mastered" && w.nextReview === "Today" && (!courseId || w.courseId === courseId)), [words]);
  const value = useMemo(() => ({
    words,
    saveWord,
    removeWord,
    isSaved,
    recordPractice,
    dueWords
  }), [words, saveWord, removeWord, isSaved, recordPractice, dueWords]);
  return <WordsContext.Provider value={value} data-id="element-1250" data-mp="3uhf2tt5057cb">{children}</WordsContext.Provider>;
}
export function useWords(): WordsContextValue {
  const ctx = useContext(WordsContext);
  if (!ctx) throw new Error("useWords must be used within WordsProvider");
  return ctx;
}